import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import SalesAgentChat from '../components/SalesAgentChat';
import {
  ArrowRight, ChevronRight, Shirt, Sparkles, Layers, Palette, Loader2,
  Phone, Mail, Clock, MapPin, Check, ShieldCheck, Truck, Award
} from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite, getBrand } from '../lib/brand';
import { BrandLogo } from '../components/BrandLogo';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/25a1855c-506f-4ba3-af07-38b64d81e107/images/66f30c3622f02e6fa3d46f0b26855c2316899fdbd6dfd4101940104ba647effb.png',
  uniform: 'https://static.prod-images.emergentagent.com/jobs/25a1855c-506f-4ba3-af07-38b64d81e107/images/3abd029f1418e0f0266b789fe0d117533d3a2648dba303a2a5c99b89c9ab49bc.png',
  screenPrint: 'https://images.unsplash.com/photo-1663433567177-9f94be0bff4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
  embroidery: 'https://images.pexels.com/photos/16430929/pexels-photo-16430929.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  sports: 'https://images.pexels.com/photos/6203632/pexels-photo-6203632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  gallery1: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&q=80&w=900',
  gallery2: 'https://images.unsplash.com/photo-1554568218-0f1715e72254?auto=format&fit=crop&q=80&w=900',
  gallery3: 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?auto=format&fit=crop&q=80&w=900',
  gallery4: 'https://images.unsplash.com/photo-1503341504253-dff4815485f1?auto=format&fit=crop&q=80&w=900',
  gallery5: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&q=80&w=900',
};

const SERVICES = [
  { icon: Layers, title: 'Screen Printing', desc: 'High-volume, vibrant prints. Industry standard for sports teams, schools, and businesses needing 24+ shirts.', tag: 'BEST FOR BULK' },
  { icon: Sparkles, title: 'Embroidery', desc: 'Premium stitched logos on polos, jackets, and caps. Built to outlast the garment. Ideal for uniforms.', tag: 'PREMIUM' },
  { icon: Palette, title: 'DTF Printing', desc: 'Direct-to-Film for photorealistic designs, full-color logos, and small batches. Zero color limits.', tag: 'FULL COLOR' },
  { icon: Shirt, title: 'Sublimation', desc: 'Permanent dye-bonded prints on performance polyester. Perfect for jerseys and athletic wear.', tag: 'ATHLETIC' },
];

const INDUSTRIES = [
  { name: 'Schools & Universities', desc: 'Spirit wear, faculty apparel, fundraising tees, graduation merchandise' },
  { name: 'Sports Teams', desc: 'Custom jerseys, warm-ups, team uniforms, tournament gear' },
  { name: 'Businesses', desc: 'Corporate uniforms, employee apparel, branded promotional wear' },
  { name: 'Events & Festivals', desc: 'Staff shirts, attendee giveaways, volunteer identification' },
  { name: 'Churches & Non-Profits', desc: 'Mission trips, volunteer teams, congregation events' },
  { name: 'Restaurants & Hospitality', desc: 'Server uniforms, kitchen wear, branded merchandise' },
];

const STEPS = [
  { num: '01', title: 'Request a Quote', desc: 'Send us your design idea, quantity, and deadline. Get pricing within 24 hours.' },
  { num: '02', title: 'Design Approval', desc: 'Review a digital mockup of your apparel. Approve online with one click.' },
  { num: '03', title: 'Production & Delivery', desc: 'We print, embroider, and ship. Most orders complete in 5 to 10 business days from artwork approval.' },
];

const FAQ_DATA = [
  { q: 'What is your minimum order?', a: 'Our standard minimum is 12 pieces for screen printing and embroidery. DTF orders can be as low as 1 piece. Sublimation jerseys start at 6 pieces.' },
  { q: 'How fast can you turn around an order?', a: 'Standard production is 5 to 10 business days from artwork approval. Rush production (3-5 days) is available for an additional fee. Need it faster? Call us — we can usually accommodate.' },
  { q: 'What file formats do you accept for artwork?', a: 'Vector files (AI, EPS, PDF, SVG) are ideal for best quality. We also accept high-resolution PNG, JPG, and PSD files at 300 DPI. Don\'t have artwork? Our design team can help create it.' },
  { q: 'Do you offer pricing for very large orders?', a: 'Absolutely. Pricing drops significantly at 72, 144, 288, and 500+ piece tiers. For enterprise or school-district orders of 1,000+, contact us directly for custom pricing.' },
  { q: 'Can you match Pantone colors?', a: 'Yes. We Pantone-match for screen printing and digital print. Embroidery thread is matched to the closest Madeira or Robison-Anton thread color.' },
  { q: 'Do you ship nationwide?', a: 'Yes — we ship anywhere in the continental US. Local pickup is available at our facility. International shipping is available by quote.' },
];

const MarketingHome = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pastWork, setPastWork] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', organization: '',
    quantity: '', deadline: '', message: ''
  });

  // Inject Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-stares-fonts]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=DM+Sans:wght@400;500;700&display=swap';
    link.setAttribute('data-stares-fonts', 'true');
    document.head.appendChild(link);
  }, []);

  // Fetch admin-curated gallery photos
  useEffect(() => {
    axios.get('/past-work').then(res => setPastWork(res.data || [])).catch(() => {});
  }, []);

  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/leads/public', form);
      setSubmitted(true);
      toast.success('Quote request sent — we\'ll be in touch within 24 hours');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please call us instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'bg-[#0A0A0A] border border-white/15 text-white placeholder:text-white/30 rounded-sm focus:border-[#D90000] focus:ring-1 focus:ring-[#D90000] h-12';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="marketing-home">
      <style>{`
        .heading-font { font-family: 'Barlow Condensed', sans-serif; letter-spacing: -0.01em; }
        .overline { font-family: 'Barlow Condensed', sans-serif; letter-spacing: 0.18em; text-transform: uppercase; }
      `}</style>

      {/* ===== HEADER ===== */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <BrandLogo height={36} className="h-9 w-auto" light />
            
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <button onClick={scrollTo('services')} className="text-white/70 hover:text-white transition" data-testid="nav-services">Services</button>
            <button onClick={scrollTo('process')} className="text-white/70 hover:text-white transition" data-testid="nav-process">Process</button>
            <button onClick={scrollTo('work')} className="text-white/70 hover:text-white transition" data-testid="nav-work">Work</button>
            <button onClick={scrollTo('faq')} className="text-white/70 hover:text-white transition" data-testid="nav-faq">FAQ</button>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="nav-login-btn">
              Customer Login
            </Button>
            <Button onClick={() => navigate('/design')} variant="outline" className="rounded-sm font-bold uppercase tracking-wide border-white/30 bg-transparent text-white hover:bg-white hover:text-black hidden md:inline-flex" data-testid="nav-design-btn">
              Design Now
            </Button>
            <Button onClick={() => navigate('/catalog')} variant="ghost" className="rounded-sm font-bold uppercase tracking-wide text-white/70 hover:text-white hover:bg-white/10 hidden md:inline-flex" data-testid="nav-catalog-btn">
              Catalog
            </Button>
            <Button onClick={scrollTo('quote')} className="bg-[#D90000] hover:bg-[#FF1A1A] text-white rounded-sm font-bold uppercase tracking-wide" data-testid="nav-quote-btn">
              Get a Quote
            </Button>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[92vh] flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="overline text-[#D90000] text-sm mb-6 flex items-center gap-3">
              <span className="h-px w-12 bg-[#D90000]" />
              {brandName().toUpperCase()} / {(getBrand().tagline || "CUSTOM APPAREL").toUpperCase()}
            </motion.div>
            <motion.h1 variants={fadeUp} className="heading-font text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] mb-6">
              CUSTOM APPAREL.<br />
              <span className="text-[#D90000]">BUILT TO PERFORM.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg sm:text-xl text-white/70 mb-10 max-w-2xl leading-relaxed">
              Premium screen printing, embroidery, and DTF for schools, sports teams,
              and businesses. Reliable quality at scale — no minimum hassles, no missed deadlines.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate('/design')} size="lg"
                className="bg-[#D90000] hover:bg-[#FF1A1A] text-white rounded-sm font-bold uppercase tracking-wider h-14 px-8 text-base"
                data-testid="hero-design-btn">
                <Sparkles className="w-5 h-5 mr-2" /> Design Now — Instant Pricing
              </Button>
              <Button onClick={scrollTo('quote')} size="lg" variant="outline"
                className="rounded-sm font-bold uppercase tracking-wider h-14 px-8 text-base border-white/30 bg-transparent text-white hover:bg-white hover:text-black"
                data-testid="hero-quote-btn">
                Request Custom Quote <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/60">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D90000]" />100% Quality Guarantee</span>
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#D90000]" />Nationwide Shipping</span>
              <span className="flex items-center gap-2"><Award className="w-4 h-4 text-[#D90000]" />10+ Years Experience</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== TRUST BANNER ===== */}
      <section className="border-y border-white/10 bg-[#D90000]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: '1M+', l: 'Shirts Printed' },
            { n: '500+', l: 'Teams Served' },
            { n: '10+', l: 'Years Operating' },
            { n: '24h', l: 'Quote Response' },
          ].map((s, i) => (
            <div key={i}>
              <div className="heading-font text-4xl sm:text-5xl font-black text-white">{s.n}</div>
              <div className="overline text-xs text-white/80 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-24 sm:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6 }} className="max-w-2xl mb-16">
            <div className="overline text-sm text-[#D90000] mb-4">Our Services</div>
            <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase mb-4">
              Four print methods.<br />One promise: it ships on time.
            </h2>
            <p className="text-white/60 text-lg">
              From small custom orders to bulk runs for entire school districts, we run the right method for your job.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {SERVICES.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-[#141414] p-8 sm:p-10 group hover:bg-[#1a1a1a] transition relative"
                  data-testid={`service-${svc.title.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="flex items-start justify-between mb-6">
                    <Icon className="w-10 h-10 text-[#D90000]" strokeWidth={1.5} />
                    <span className="overline text-[10px] text-white/40 border border-white/15 px-2 py-1">{svc.tag}</span>
                  </div>
                  <h3 className="heading-font text-3xl font-black uppercase mb-3">{svc.title}</h3>
                  <p className="text-white/60 leading-relaxed mb-6">{svc.desc}</p>
                  <button onClick={scrollTo('quote')} className="overline text-sm text-[#D90000] flex items-center gap-2 group-hover:gap-3 transition-all">
                    Get a Quote <ChevronRight className="w-4 h-4" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== WHO WE SERVE ===== */}
      <section className="py-24 sm:py-32 px-6 bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline text-sm text-[#D90000] mb-4">Who We Serve</div>
            <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase mb-6 leading-[0.95]">
              We build apparel<br />for serious organizations.
            </h2>
            <p className="text-white/60 text-lg mb-10">
              If you need quality, consistency, and on-time delivery — you're our customer.
              We don't do one-off novelty work.
            </p>
            <div className="space-y-px bg-white/10 border border-white/10">
              {INDUSTRIES.map((ind, i) => (
                <div key={i} className="bg-[#0F0F0F] p-5 flex items-start gap-4 hover:bg-[#161616] transition group">
                  <Check className="w-5 h-5 text-[#D90000] flex-shrink-0 mt-1" />
                  <div>
                    <div className="heading-font font-bold uppercase text-lg tracking-tight">{ind.name}</div>
                    <div className="text-white/50 text-sm mt-0.5">{ind.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={IMAGES.sports} alt="Sports team" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="overline text-xs text-[#D90000] mb-2">Featured</div>
              <div className="heading-font text-2xl font-black uppercase">Sports Teams &amp; Athletic Programs</div>
              <p className="text-white/70 text-sm mt-2">From little league to college — we kit teams of every level.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="process" className="py-24 sm:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <div className="overline text-sm text-[#D90000] mb-4">How It Works</div>
            <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase mb-4">
              Three steps.<br />Zero guesswork.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {STEPS.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#0A0A0A] p-8 sm:p-12 relative">
                <div className="heading-font text-7xl font-black text-white/5 absolute top-6 right-6 leading-none">{step.num}</div>
                <div className="overline text-[#D90000] text-sm mb-4">Step {step.num}</div>
                <h3 className="heading-font text-2xl sm:text-3xl font-black uppercase mb-3">{step.title}</h3>
                <p className="text-white/60 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GALLERY / WORK ===== */}
      <section id="work" className="py-24 sm:py-32 px-6 bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="overline text-sm text-[#D90000] mb-4">Recent Work</div>
              <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase">
                The proof is in the prints.
              </h2>
            </div>
            <Button onClick={scrollTo('quote')} variant="outline"
              className="rounded-sm border-white/30 bg-transparent text-white hover:bg-white hover:text-black uppercase font-bold tracking-wider">
              Start Your Project
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pastWork.length > 0 ? (
              pastWork.slice(0, 5).map((item, i) => (
                <div key={item.id}
                  className={`overflow-hidden bg-black ${i === 0 ? 'md:col-span-2 md:row-span-2 aspect-square' : 'aspect-square'}`}>
                  <img src={item.image_base64} alt={item.caption || ''}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              ))
            ) : (
              <>
                <div className="md:col-span-2 md:row-span-2 aspect-square overflow-hidden bg-black">
                  <img src={IMAGES.uniform} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={IMAGES.screenPrint} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={IMAGES.embroidery} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={IMAGES.gallery1} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={IMAGES.gallery2} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="overline text-sm text-[#D90000] mb-4">What Customers Say</div>
          <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase mb-16 max-w-3xl">
            Trusted by teams, schools, and brands across Florida.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { quote: `${brandName()} has been our go-to for team uniforms for 5 years. The quality is unmatched and they've never missed a deadline.`, name: 'Athletic Director', org: 'Local High School' },
              { quote: 'We outfit our entire staff of 80 with custom polos every year. The embroidery looks premium and lasts season after season.', name: 'HR Manager', org: 'Hospitality Group' },
              { quote: 'Called them on a Tuesday for 200 event shirts needed by Friday. They delivered on time and they looked incredible.', name: 'Event Coordinator', org: 'Charity 5K' },
            ].map((t, i) => (
              <div key={i} className="bg-[#0F0F0F] p-8 sm:p-10">
                <div className="heading-font text-6xl text-[#D90000] leading-none mb-4">"</div>
                <p className="text-white/80 leading-relaxed mb-6">{t.quote}</p>
                <div className="border-t border-white/10 pt-4">
                  <div className="heading-font font-bold uppercase">{t.name}</div>
                  <div className="text-white/50 text-sm">{t.org}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-24 sm:py-32 px-6 bg-[#0F0F0F]">
        <div className="max-w-4xl mx-auto">
          <div className="overline text-sm text-[#D90000] mb-4">Frequently Asked</div>
          <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase mb-12">
            Questions, answered.
          </h2>
          <Accordion type="single" collapsible className="space-y-px bg-white/10 border border-white/10">
            {FAQ_DATA.map((item, i) => (
              <AccordionItem key={i} value={`q${i}`} className="bg-[#0A0A0A] border-0">
                <AccordionTrigger className="heading-font font-bold uppercase tracking-tight text-lg px-6 py-5 hover:no-underline hover:bg-[#141414] text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 text-white/60 leading-relaxed text-base">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== GET A QUOTE FORM ===== */}
      <section id="quote" className="py-24 sm:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="overline text-sm text-[#D90000] mb-4">Start Your Order</div>
              <h2 className="heading-font text-4xl sm:text-5xl font-black uppercase mb-6 leading-[0.95]">
                Get a quote.<br />Same day.
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Fill out the form and we'll send pricing within 24 hours — usually faster.
                No spam, no high-pressure sales. Just numbers.
              </p>
              <div className="space-y-5 mt-12">
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-[#D90000] mt-1" />
                  <div>
                    <div className="overline text-xs text-white/40">Call us direct</div>
                    <a href={`tel:${brandPhone()}`} className="heading-font text-xl font-bold hover:text-[#D90000]">{brandPhone()}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#D90000] mt-1" />
                  <div>
                    <div className="overline text-xs text-white/40">Email us</div>
                    <a href={`mailto:${brandEmail()}`} className="heading-font text-xl font-bold hover:text-[#D90000]">{brandEmail()}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-[#D90000] mt-1" />
                  <div>
                    <div className="overline text-xs text-white/40">Hours</div>
                    <div className="text-white/80">Mon–Fri · 9am–6pm EST</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#141414] border border-white/10 p-8 sm:p-10">
              {submitted ? (
                <div className="text-center py-12" data-testid="quote-submitted-confirmation">
                  <div className="w-16 h-16 bg-[#D90000] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="heading-font text-3xl font-black uppercase mb-3">Got it.</h3>
                  <p className="text-white/60">
                    Your quote request is in. We'll reach out within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
                  <div className="grid grid-cols-2 gap-3">
                    <Input className={inputCls} placeholder="Your name *" required
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="quote-name" />
                    <Input className={inputCls} placeholder="Organization"
                      value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                      data-testid="quote-org" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="email" className={inputCls} placeholder="Email *" required
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="quote-email" />
                    <Input type="tel" className={inputCls} placeholder="Phone"
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="quote-phone" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input className={inputCls} placeholder="Quantity (e.g. 50)"
                      value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      data-testid="quote-qty" />
                    <Input className={inputCls} placeholder="Need by date"
                      value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                      data-testid="quote-deadline" />
                  </div>
                  <Textarea className={`${inputCls} min-h-32 h-auto py-3`}
                    placeholder="Tell us about your project — designs, colors, garment style, etc."
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="quote-message" />
                  <Button type="submit" disabled={submitting} size="lg"
                    className="w-full bg-[#D90000] hover:bg-[#FF1A1A] text-white rounded-sm font-bold uppercase tracking-wider h-14"
                    data-testid="quote-submit-btn">
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <>Send Quote Request <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                  <p className="text-xs text-white/40 text-center pt-2">
                    We typically respond within 24 hours. By submitting you agree to receive emails about your quote.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-white/10 bg-black pt-20 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <BrandLogo height={48} className="h-12 w-auto" light />
                
              </div>
              <p className="text-white/50 max-w-md leading-relaxed">
                Commercial custom apparel. Built for teams, schools, and businesses that need quality at scale.
              </p>
            </div>
            <div>
              <div className="overline text-xs text-white/40 mb-4">Contact</div>
              <ul className="space-y-2 text-white/70 text-sm">
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> {brandPhone()}</li>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> {brandEmail()}</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Florida, USA</li>
              </ul>
            </div>
            <div>
              <div className="overline text-xs text-white/40 mb-4">Services</div>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><button onClick={scrollTo('services')} className="hover:text-[#D90000]">Screen Printing</button></li>
                <li><button onClick={scrollTo('services')} className="hover:text-[#D90000]">Embroidery</button></li>
                <li><button onClick={scrollTo('services')} className="hover:text-[#D90000]">DTF Printing</button></li>
                <li><button onClick={scrollTo('services')} className="hover:text-[#D90000]">Sublimation</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-white/40 text-xs">© {new Date().getFullYear()} {brandName()}. All rights reserved.</div>
            <button onClick={() => navigate('/login')} className="overline text-xs text-white/40 hover:text-white" data-testid="footer-login-btn">
              Customer Portal Login →
            </button>
          </div>
        </div>
      </footer>

      {/* Floating AI Sales Agent */}
      <SalesAgentChat />
    </div>
  );
};

export default MarketingHome;
